// The Reports screen's arithmetic: periods, the fields the matrix may be laid
// out by, the figures each cell reads, and the worksheets a visual exports.
// Plain functions; the page memoises them.
//
// Everything here is read from the server's own aggregates. The report the
// server sends is already grouped — by service, by hour, by desk, by person, by
// outcome, by mark — each group carrying its counts, its waiting and handling
// times (median, ninetieth, mean, longest) and its service level. It is never a
// list of visits. So the matrix is laid out by what the server grouped by: one
// grouping beside the period before, or two crossed (report/cross), whose cells
// the server counts and whose totals are each grouping's own rows.

import { networkCells } from '../lib/network'
import { formatMoment, t, type StaffKey, type StaffLanguage } from '../lib/staffText'
import type { BandSpec, BreakRow, CrossCell, DeliveredRow, FetchedDim, NetworkBranchLine, ReportRow, ReportSummary, TimeRow } from '../lib/types'
import type { XCell, XFmt, XSheet } from './xlsx'

// ---------------------------------------------------------------- numbers

/** A number a person reads at a glance: 1 240 rather than 1240. */
export function group(n: number): string {
  return Math.round(n).toLocaleString('en-GB').replace(/,/g, ' ')
}

/** A time as a person says it, as number and unit pairs: 14 s; 9 min 28 s; 48 h 50 min.
 *  Seconds go once there are hours; a zero second or minute part is left off. */
export function durationParts(seconds: number, lang: StaffLanguage): [number, string][] {
  const s = Math.max(0, Math.round(seconds))
  const sec = t('insight.unit.s', lang)
  const min = t('insight.minutes', lang)
  if (s < 60) return [[s, sec]]
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  if (h > 0) return m > 0 ? [[h, t('insight.unit.h', lang)], [m, min]] : [[h, t('insight.unit.h', lang)]]
  return s % 60 > 0 ? [[m, min], [s % 60, sec]] : [[m, min]]
}

/** A wait or a handling time in words, a number never parted from its unit: "9 min 28 s". */
export function duration(seconds: number, lang: StaffLanguage): string {
  return durationParts(seconds, lang)
    .map(([n, u]) => `${n}\u00a0${u}`)
    .join(' ')
}

export const percent = (fraction: number) => `${(fraction * 100).toFixed(1)}%`

/** The delivered table's line in words: the times are a dash when none of the
 *  entries had anything to be timed from, and the outcome is a dash when the
 *  delivered service has none. "0:00" and "nothing to time it from" are
 *  different statements. */
export function deliveredCells(row: DeliveredRow, lang: StaffLanguage): {
  service: string
  delivered: string
  outcome: string
  count: string
  total: string
  average: string
} {
  return {
    service: row.serviceCode,
    delivered: row.deliveredCode,
    outcome: row.outcomeCode || '—',
    count: String(row.count),
    total: row.timed === 0 ? '—' : duration(row.totalSec, lang),
    average: row.timed === 0 ? '—' : duration(row.meanSec, lang),
  }
}

/** One line of the time tables in words. Time at a desk is written the way the
 *  counter and the floor write it ("20s", "4m 12s"): as a clock, twenty seconds
 *  read "0:20", which a manager could not tell from twenty minutes. Utilisation
 *  and the rate are a dash when nothing was available to measure them against. */
export function timeCells(row: TimeRow, lang: StaffLanguage): {
  label: string
  sessions: string
  signedIn: string
  paused: string
  pending: string
  lunch: string
  shortBreak: string
  task: string
  breakEarlier: string
  serving: string
  wrapUp: string
  idle: string
  utilisation: string
  served: string
  perHour: string
} {
  const inWords = (seconds: number) => duration(seconds, lang)
  const none = row.availableSec <= 0
  return {
    label: row.label || '—',
    sessions: String(row.sessions),
    signedIn: inWords(row.signedInSec),
    paused: inWords(row.pausedSec),
    pending: inWords(row.pendingSec),
    lunch: inWords(row.lunchSec),
    shortBreak: inWords(row.shortBreakSec),
    task: inWords(row.taskSec),
    breakEarlier: inWords(row.breakEarlierSec),
    serving: inWords(row.servingSec),
    wrapUp: inWords(row.wrapUpSec),
    idle: inWords(row.idleSec),
    utilisation: none ? '—' : percent(row.utilisation),
    served: String(row.served),
    perHour: none ? '—' : row.servedPerHour.toFixed(1),
  }
}

// ---------------------------------------------------------------- breaks

/** The Kind column's words — close has no time-table column of its own, so it
 *  is the one kind named only here. */
export const BREAK_KIND: Record<string, StaffKey> = {
  lunch: 'reports.lunch',
  short_break: 'reports.shortBreak',
  task: 'reports.task',
  close: 'reports.kindClose',
}

/** The Decision column's words — the five org.RequestState values. */
export const BREAK_STATE: Record<string, StaffKey> = {
  pending: 'reports.statePending',
  approved: 'reports.stateApproved',
  declined: 'reports.stateDeclined',
  cancelled: 'reports.stateCancelled',
  auto: 'reports.stateAuto',
}

/** One line of the Breaks table in words. A request that never started a
 *  break (still pending, declined, cancelled, or a close, which is a moment
 *  and never a break) shows a dash for started, ended and length rather than
 *  "0:00" — the same distinction Measured draws for waiting and handling. */
export function breakCells(
  row: BreakRow,
  lang: StaffLanguage,
  say: (k: StaffKey, vars?: Record<string, string | number>) => string,
  zone: string | undefined,
): {
  staffName: string
  counterCode: string
  kind: string
  minutes: string
  requestedAt: string
  state: string
  decidedByName: string
  startedAt: string
  endedAt: string
  length: string
} {
  const at = (iso: string | undefined) => (iso ? formatMoment(iso, lang, { date: true, time: true, zone }) : '—')
  return {
    staffName: row.staffName || '—',
    counterCode: row.counterCode || '—',
    kind: say(BREAK_KIND[row.kind] ?? 'reports.task'),
    minutes: row.minutes === undefined ? '—' : String(row.minutes),
    requestedAt: at(row.requestedAt),
    state: say(BREAK_STATE[row.state] ?? 'reports.stateAuto'),
    decidedByName: row.decidedByName || '—',
    startedAt: at(row.startedAt),
    endedAt: at(row.endedAt),
    length: row.seconds > 0 ? duration(row.seconds, lang) : '—',
  }
}

// ---------------------------------------------------------------- periods

/** Today by the browser's calendar. The server's business date can differ after
 *  midnight at a branch open late; for a picker this is what the person expects. */
export const isoToday = () => new Date().toLocaleDateString('en-CA')

export function shiftDays(iso: string, days: number): string {
  const d = new Date(iso + 'T00:00:00')
  d.setDate(d.getDate() + days)
  return d.toLocaleDateString('en-CA')
}

const monthStart = (today: string) => today.slice(0, 8) + '01'

/** The Monday of this date's ISO week. */
export function mondayOf(iso: string): string {
  const dow = new Date(iso + 'T00:00:00Z').getUTCDay()
  return shiftDays(iso, -((dow + 6) % 7))
}

/** The first of the month n months before this date's month. */
function monthsBack(iso: string, n: number): string {
  const m = Number(iso.slice(0, 4)) * 12 + Number(iso.slice(5, 7)) - 1 - n
  return `${Math.floor(m / 12)}-${String((m % 12) + 1).padStart(2, '0')}-01`
}

export type PeriodId = 'today' | 'yesterday' | 'week' | 'month' | 'lastMonth' | 'weeks12' | 'months12' | 'custom'
export const PERIODS: ReadonlyArray<[PeriodId, StaffKey]> = [
  ['today', 'reports.today'],
  ['yesterday', 'reports.yesterday'],
  ['week', 'reports.last7'],
  ['month', 'reports.thisMonth'],
  ['lastMonth', 'insight.lastMonth'],
  ['weeks12', 'insight.weeks12'],
  ['months12', 'insight.months12'],
  ['custom', 'insight.dates'],
]

export function periodRange(p: PeriodId, today: string, custom: [string, string]): [string, string] {
  switch (p) {
    case 'today':
      return [today, today]
    case 'yesterday':
      return [shiftDays(today, -1), shiftDays(today, -1)]
    case 'week':
      return [shiftDays(today, -6), today]
    case 'month':
      return [monthStart(today), today]
    case 'lastMonth':
      return [monthsBack(today, 1), shiftDays(monthStart(today), -1)]
    case 'weeks12':
      // Twelve week rows: from the Monday eleven weeks before this week's.
      return [shiftDays(mondayOf(today), -77), today]
    case 'months12':
      return [monthsBack(today, 11), today]
    default:
      return custom
  }
}

/**
 * The period immediately before this one, of the same length.
 *
 * Same length rather than "yesterday" or "last month", because the comparison has
 * to be like for like: a three-day range compared against one day would make every
 * figure look like a collapse.
 */
export function previousPeriod(from: string, to: string): { from: string; to: string } {
  const days = Math.max(
    1,
    Math.round((Date.parse(to + 'T00:00:00') - Date.parse(from + 'T00:00:00')) / 86_400_000) + 1,
  )
  return { from: shiftDays(from, -days), to: shiftDays(from, -1) }
}

/** What a period is compared with, decided from its dates as the server's
 *  report.Range.Prior does (one table pins both: internal/report/testdata/prior-periods.json):
 *  a whole calendar month with the calendar month before it, anything else with
 *  the same number of days just before. */
export function priorPeriod(from: string, to: string): { from: string; to: string } {
  const wholeMonth = /^\d{4}-\d{2}-01$/.test(from) && to === shiftDays(monthsBack(from, -1), -1)
  if (wholeMonth) return { from: monthsBack(from, 1), to: shiftDays(from, -1) }
  return previousPeriod(from, to)
}

// ---------------------------------------------------------------- fields

export type { FetchedDim }
/** What the server groups a report by: the summary's own groupings and the fetched ones. */
export type BreakDim = 'service' | 'serviceWorked' | 'desk' | 'person' | 'hour' | 'outcome' | 'mark' | FetchedDim
/** The period is the one axis the screen adds itself: this one, and the one before. */
export type DimId = BreakDim | 'period'
export type MeasureId =
  | 'arrived'
  | 'served'
  | 'noShow'
  | 'voided'
  | 'cancelledBeforeCall'
  | 'left'
  | 'unserved'
  | 'stillOpen'
  | 'wait'
  | 'handling'
  | 'level'
  | 'noShowRate'
  | 'recalled'
export type FieldId = DimId | MeasureId
/** The four figures the server keeps for every set of times. */
export type Stat = 'median' | 'p90' | 'mean' | 'max'
export type ValueSpec = { measure: MeasureId; stat?: Stat }
export type Layout = { rows: DimId[]; cols: DimId[]; values: ValueSpec[] }
export type Fmt = 'int' | 'dur' | 'pct'

export const FETCHED: readonly FetchedDim[] = ['day', 'week', 'month', 'weekday', 'interval15', 'interval30', 'channel', 'priority', 'language', 'call']
/** Time groupings: their members read in time order, whatever order they came in. */
const TIME_DIMS: readonly FetchedDim[] = ['day', 'week', 'month', 'weekday', 'interval15', 'interval30']
/** Calendar groupings: a week of this period has no twin in the period before,
 *  so the period before is neither fetched nor shown beside them. */
export const CALENDAR: ReadonlySet<FieldId> = new Set<FieldId>(['day', 'week', 'month'])
export const BREAK_DIMS: BreakDim[] = ['service', 'serviceWorked', 'hour', 'desk', 'person', 'outcome', 'mark', ...FETCHED]
export const DIMS: DimId[] = [...BREAK_DIMS, 'period']
export const MEASURES: MeasureId[] = ['arrived', 'served', 'wait', 'handling', 'level', 'noShowRate', 'noShow', 'recalled', 'left', 'unserved', 'voided', 'cancelledBeforeCall', 'stillOpen']
export const STATS: Stat[] = ['median', 'p90', 'mean', 'max']
export const MAX_VALUES = 4
export const MAX_ROWS = 2
export const MAX_COLS = 1

export const DIM_KEY: Record<DimId, StaffKey> = {
  service: 'insight.dim.service',
  serviceWorked: 'insight.dim.serviceWorked',
  hour: 'insight.dim.hour',
  desk: 'insight.dim.desk',
  person: 'insight.dim.person',
  outcome: 'insight.dim.outcome',
  mark: 'insight.dim.mark',
  day: 'insight.dim.day',
  week: 'insight.dim.week',
  month: 'insight.dim.month',
  weekday: 'insight.dim.weekday',
  interval15: 'insight.dim.interval15',
  interval30: 'insight.dim.interval30',
  channel: 'insight.dim.channel',
  priority: 'common.priority',
  language: 'entry.language',
  call: 'insight.dim.call',
  period: 'insight.dim.period',
}

/** The field list's groups, one word each. The period sits with When. */
export const FIELD_GROUPS: ReadonlyArray<{ key: StaffKey; dims: BreakDim[] }> = [
  { key: 'insight.group.what', dims: ['service', 'serviceWorked', 'outcome', 'mark'] },
  { key: 'insight.group.who', dims: ['desk', 'person'] },
  { key: 'insight.group.when', dims: ['day', 'week', 'month', 'weekday', 'hour', 'interval30', 'interval15'] },
  { key: 'insight.group.how', dims: ['channel', 'priority', 'language', 'call'] },
]

export const MEASURE_KEY: Record<MeasureId, StaffKey> = {
  arrived: 'reports.arrived',
  served: 'reports.served',
  wait: 'insight.m.wait',
  handling: 'insight.m.handling',
  level: 'reports.serviceLevel',
  noShowRate: 'insight.m.noShowRate',
  noShow: 'insight.m.noShow',
  left: 'reports.leftFromPhone',
  unserved: 'reports.leftUnservedShort',
  voided: 'reports.voided',
  cancelledBeforeCall: 'reports.cancelledBeforeCall',
  stillOpen: 'reports.stillOpen',
  recalled: 'insight.m.recalled',
}

export const STAT_KEY: Record<Stat, StaffKey> = {
  median: 'insight.stat.median',
  p90: 'insight.stat.p90',
  mean: 'insight.stat.mean',
  max: 'insight.stat.max',
}

export const isMeasure = (f: FieldId): f is MeasureId => (MEASURES as string[]).includes(f)
export const isBreak = (f: FieldId): f is BreakDim => (BREAK_DIMS as string[]).includes(f)
export const isFetched = (f: FieldId): f is FetchedDim => (FETCHED as string[]).includes(f)
export const isTimed = (m: MeasureId) => m === 'wait' || m === 'handling'
export const fmtOf = (m: MeasureId): Fmt => (isTimed(m) ? 'dur' : m === 'level' || m === 'noShowRate' ? 'pct' : 'int')

/**
 * The breakdowns that count tickets. Every other one counts pieces of work: a
 * desk, a person, a service delivered, an outcome, a mark. A ticket is not
 * issued at a desk and a wait is not caused by the desk that finally called the
 * customer, so arrivals, waits and the service level have no figure there — a
 * dash, not a zero, because zero would say "nobody waited at desk 3".
 */
const TICKET_DIMS = new Set<BreakDim>(['service', 'hour', ...FETCHED])
const TICKET_MEASURES = new Set<MeasureId>(['arrived', 'noShow', 'voided', 'cancelledBeforeCall', 'left', 'unserved', 'stillOpen', 'wait', 'level', 'noShowRate', 'recalled'])
const COUNT_MEASURES = new Set<MeasureId>(['arrived', 'served', 'noShow', 'voided', 'cancelledBeforeCall', 'left', 'unserved', 'stillOpen', 'recalled'])

export const applies = (m: MeasureId, dim: BreakDim | undefined) => !dim || TICKET_DIMS.has(dim) || !TICKET_MEASURES.has(m)
export const isTicketDim = (d: BreakDim) => TICKET_DIMS.has(d)

/** The report with each service row labelled by its name in the reader's language,
 *  as the PDF reads it; the code where the service has none. The file keeps both. */
export function withNames(s: ReportSummary): ReportSummary {
  const named = <T extends ReportRow[] | undefined>(rows: T): T => rows?.map((r) => (r.name ? { ...r, label: r.name } : r)) as T
  return { ...s, byService: named(s.byService), byServiceWorked: named(s.byServiceWorked) }
}

/** Two groupings crossed: the cells as the server sent them, by its a and b. */
export type CrossSource = { a: BreakDim; b: BreakDim; cells: CrossCell[] }
/** A period's report plus the groupings fetched for it, and the cross of a layout
 *  of two groupings (null until it arrives). */
export type Source = { summary: ReportSummary; extra: Partial<Record<FetchedDim, ReportRow[]>>; cross?: CrossSource | null }

/** The two groupings a layout crosses, in the order placed, or null. */
export function crossOf(layout: Layout): [BreakDim, BreakDim] | null {
  const b = [...layout.rows, ...layout.cols].filter(isBreak)
  return b.length === 2 ? [b[0]!, b[1]!] : null
}

const cellIndex = new WeakMap<CrossCell[], Map<string, CrossCell>>()
/** The cell of a cross at two members (each keyed key || label, as the matrix keys
 *  a member); undefined where it has none, null where the cross is not this one or
 *  has not arrived. */
export function crossCell(src: Source, d1: BreakDim, k1: string, d2: BreakDim, k2: string): CrossCell | undefined | null {
  const c = src.cross
  if (!c) return null
  const swap = c.a === d2 && c.b === d1
  if (!swap && (c.a !== d1 || c.b !== d2)) return null
  let index = cellIndex.get(c.cells)
  if (!index) {
    index = new Map(c.cells.map((x) => [`${x.a || x.aLabel}\u0001${x.b || x.bLabel}`, x] as const))
    cellIndex.set(c.cells, index)
  }
  return index.get(swap ? `${k2}\u0001${k1}` : `${k1}\u0001${k2}`)
}

/** A grouping's rows, or null for a fetched one that has not arrived yet. */
export function rowsOf(src: Source, d: BreakDim): ReportRow[] | null {
  if (isFetched(d)) return src.extra[d] ?? null
  const s = src.summary
  switch (d) {
    case 'service':
      return s.byService ?? []
    case 'serviceWorked':
      return s.byServiceWorked ?? []
    case 'desk':
      return s.byCounter ?? []
    case 'person':
      return s.byOperator ?? []
    case 'hour':
      return s.byHour ?? []
    case 'outcome':
      return s.byOutcome?.length ? s.byOutcome : s.outcomeUnrecorded ? [s.outcomeUnrecorded] : []
    case 'mark':
      return s.byMark ?? []
  }
}

type Figures = Pick<ReportRow, 'counts' | 'waiting' | 'handling' | 'serviceLevel'>

/** One figure off a row of the report, or null where there is nothing to measure. */
export function figure(r: Figures | undefined, v: ValueSpec, dim?: BreakDim): number | null {
  if (!applies(v.measure, dim)) return null
  // A group missing from one period had nothing in it: its counts are zero, its
  // times have no figure.
  if (!r) return COUNT_MEASURES.has(v.measure) ? 0 : null
  const c = r.counts
  switch (v.measure) {
    case 'arrived':
      return c.issued
    case 'served':
      return c.served
    case 'noShow':
      return c.noShow
    case 'voided':
      return c.voided
    case 'cancelledBeforeCall':
      // A server from before this figure sends none: a dash, not a zero.
      return c.outcome?.leftBeforeCall ?? null
    case 'left':
      return c.left
    case 'unserved':
      return c.unserved
    case 'stillOpen':
      return c.stillOpen
    case 'recalled':
      // A server from before this figure sends none: a dash, not a zero.
      return c.recalled ?? null
    case 'wait':
    case 'handling': {
      const d = v.measure === 'wait' ? r.waiting : r.handling
      if (d.count === 0) return null
      const stat = v.stat ?? 'median'
      return stat === 'p90' ? d.p90Sec : stat === 'mean' ? d.meanSec : stat === 'max' ? d.maxSec : d.medianSec
    }
    case 'level':
      return r.serviceLevel.measured === 0 ? null : r.serviceLevel.within / r.serviceLevel.measured
    case 'noShowRate':
      return r.waiting.count === 0 ? null : c.noShow / r.waiting.count
  }
}

export function formatValue(x: number | null, f: Fmt, lang: StaffLanguage): string {
  if (x === null || !Number.isFinite(x)) return '—'
  if (f === 'dur') return (x < 0 ? '−' : '') + duration(Math.abs(x), lang)
  if (f === 'pct') return percent(x)
  return group(x)
}

/** A change, signed: +12, −3.4 pts, +45 s. */
export function formatChange(x: number | null, f: Fmt, pts: (n: string) => string, lang: StaffLanguage): string {
  if (x === null || !Number.isFinite(x)) return '—'
  const sign = x > 0 ? '+' : x < 0 ? '−' : '±'
  if (f === 'dur') return sign + duration(Math.abs(x), lang)
  if (f === 'pct') return sign + pts((Math.abs(x) * 100).toFixed(1))
  return sign + group(Math.abs(x))
}

// ---------------------------------------------------------------- the matrix

export type PeriodKey = 'now' | 'before' | 'change'
/** Where a cell sits: which period, which group of the breakdown, and which group
 *  of a second breakdown crossed with it. */
export type Spot = { period?: PeriodKey; dim?: BreakDim; key?: string; also?: { dim: BreakDim; key: string } }

/** a's place narrowed by b's: b's grouping replaces a's own, else becomes the second. */
export function joinSpot(a: Spot, b: Spot): Spot {
  const out: Spot = { ...a }
  if (b.period) out.period = b.period
  const add = (dim: BreakDim, key: string) => {
    if (!out.dim || out.dim === dim) {
      out.dim = dim
      out.key = key
    } else out.also = { dim, key }
  }
  if (b.dim) add(b.dim, b.key ?? '')
  if (b.also) add(b.also.dim, b.also.key)
  return out
}

/** Each grouping a spot names, with its member. */
export const spotDims = (p: Spot): { dim: BreakDim; key: string }[] => [...(p.dim ? [{ dim: p.dim, key: p.key ?? '' }] : []), ...(p.also ? [p.also] : [])]
export type Member = { key: string; label: string }
export type MRow = { id: string; label: string; level: number; group: boolean; pick: Spot; dim?: DimId }
export type MCol = { id: string; label: string; pick: Spot; kind: 'leaf' | 'total' | 'change' }
export type MatrixSort = { col: string; vi: number; dir: 1 | -1 } | null
export type Matrix = {
  rows: MRow[]
  cols: MCol[]
  /** The grand total line, or null where the layout has none (the period on rows). */
  total: MRow | null
  value: (row: Spot, col: Spot, vi: number) => number | null
}
export type Words = {
  now: string
  before: string
  change: string
  total: string
  blank: string
  /** A fetched grouping's member in words; the server sends only its key. */
  member?: (dim: FetchedDim, key: string) => string
  /** The period read: a calendar grouping shows every day, week or month in it. */
  range?: { from: string; to: string }
  /** The branch's day starts this many minutes after midnight: times of day read from it. */
  rollover?: number
}

/** Times of day: their members read in the business day's order. */
const CLOCK_DIMS: ReadonlySet<DimId> = new Set<DimId>(['hour', 'interval15', 'interval30'])

/** Where a time of day ("09" or "09:30") falls in a business day that starts
 *  rollover minutes after midnight: 01:00 comes after 23:00 when the day starts at
 *  02:00. A key that is not a time sorts last. */
export function dayOrder(key: string, rollover = 0): number {
  const m = /^(\d{2})(?::(\d{2}))?$/.exec(key)
  if (!m) return Number.MAX_SAFE_INTEGER
  const min = Number(m[1]) * 60 + Number(m[2] ?? 0)
  return (((min - rollover) % 1440) + 1440) % 1440
}

/** Every member a time grouping has over a period, the empty ones included: the
 *  seven weekdays, and each day, week (by its Monday) or month in from..to.
 *  Null for any other grouping or a period that is not two dates. */
export function calendarKeys(dim: DimId, from: string, to: string): string[] | null {
  if (dim === 'weekday') return ['1', '2', '3', '4', '5', '6', '7']
  const iso = /^\d{4}-\d{2}-\d{2}$/
  if (!iso.test(from) || !iso.test(to) || from > to) return null
  const out: string[] = []
  if (dim === 'day') for (let d = from; d <= to && out.length < 1100; d = shiftDays(d, 1)) out.push(d)
  else if (dim === 'week') for (let d = mondayOf(from); d <= to && out.length < 160; d = shiftDays(d, 7)) out.push(d)
  else if (dim === 'month') for (let m = from.slice(0, 7); m <= to.slice(0, 7) && out.length < 40; m = monthsBack(m + '-01', -1).slice(0, 7)) out.push(m)
  else return null
  return out
}

/** How many days a period spans, both ends counted; 0 for one that is not two dates. */
export function daysIn(from: string, to: string): number {
  const d = Math.round((Date.parse(to + 'T00:00:00Z') - Date.parse(from + 'T00:00:00Z')) / 86_400_000) + 1
  return Number.isFinite(d) && d > 0 ? d : 0
}

/** An hour row's "09:00" as the hour it starts, "09:00–10:00", as the 15- and
 *  30-minute rows read; anything else as it is. Go: report.hourSpan. */
export function hourSpan(label: string): string {
  const m = /^(\d{2}):00$/.exec(label)
  if (!m || Number(m[1]) > 23) return label
  return `${label}–${String((Number(m[1]) + 1) % 24).padStart(2, '0')}:00`
}

export function members(src: { now: Source; before: Source | null }, dim: DimId, w: Words): Member[] {
  if (dim === 'period') {
    return [
      { key: 'now', label: w.now },
      { key: 'before', label: w.before },
      { key: 'change', label: w.change },
    ]
  }
  const seen = new Map<string, string>()
  for (const s of CALENDAR.has(dim) ? [src.now] : [src.now, src.before]) {
    if (!s) continue
    for (const r of rowsOf(s, dim) ?? []) {
      const key = r.key || r.label
      // An outcome with no key is the server's "not recorded", said in the reader's words.
      if (!seen.has(key)) seen.set(key, isFetched(dim) && w.member ? w.member(dim, key) : dim === 'hour' ? hourSpan(r.label) : (dim === 'outcome' && !r.key) || !r.label ? w.blank : r.label)
    }
  }
  // A time grouping shows its empty periods too, as zero rows, once its rows are here.
  const all = w.range && isFetched(dim) && rowsOf(src.now, dim) !== null ? calendarKeys(dim, w.range.from, w.range.to) : null
  for (const key of all ?? []) if (!seen.has(key)) seen.set(key, w.member ? w.member(dim as FetchedDim, key) : key)
  const out = [...seen].map(([key, label]) => ({ key, label }))
  if (CLOCK_DIMS.has(dim)) return out.sort((a, b) => dayOrder(a.key, w.rollover) - dayOrder(b.key, w.rollover))
  return (TIME_DIMS as string[]).includes(dim) ? out.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0)) : out
}

const pickFor = (dim: DimId, key: string): Spot =>
  dim === 'period' ? { period: key as PeriodKey } : { dim, key }

export function buildMatrix(
  src: { now: Source; before: Source | null },
  layout: Layout,
  w: Words,
  sort: MatrixSort,
): Matrix {
  const hasPeriod = layout.rows.includes('period') || layout.cols.includes('period')
  // Beside a calendar grouping the period is this one alone.
  const calendar = [...layout.rows, ...layout.cols].some((d) => CALENDAR.has(d))
  const membersOf = (dim: DimId) => (dim === 'period' && calendar ? members(src, dim, w).slice(0, 1) : members(src, dim, w))

  const value = (a: Spot, b: Spot, vi: number): number | null => {
    const v = layout.values[vi]
    if (!v) return null
    const p = joinSpot(a, b)
    // With the period laid out, a cell that names no period would be neither
    // period, so it stays empty rather than quietly showing this one.
    if (hasPeriod && !p.period) return null
    if (p.period === 'change') {
      const now = value({ ...p, period: 'now' }, {}, vi)
      const before = value({ ...p, period: 'before' }, {}, vi)
      return now === null || before === null ? null : now - before
    }
    const s = p.period === 'before' ? src.before : src.now
    if (!s) return null
    if (!p.dim) return figure(s.summary, v)
    if (p.period === 'before' && CALENDAR.has(p.dim)) return null
    if (p.also) {
      if (p.period === 'before' || !applies(v.measure, p.also.dim)) return null
      const cell = crossCell(s, p.dim, p.key ?? '', p.also.dim, p.also.key)
      return cell === null ? null : figure(cell, v, p.dim)
    }
    const rows = rowsOf(s, p.dim)
    if (!rows) return null
    return figure(
      rows.find((r) => (r.key || r.label) === p.key),
      v,
      p.dim,
    )
  }

  // ---- columns
  const cols: MCol[] = []
  const colDim = layout.cols[0]
  if (!colDim) cols.push({ id: '', label: '', pick: {}, kind: 'leaf' })
  else {
    for (const m of membersOf(colDim)) {
      cols.push({ id: m.key, label: m.label, pick: pickFor(colDim, m.key), kind: colDim === 'period' && m.key === 'change' ? 'change' : 'leaf' })
    }
    if (colDim !== 'period') cols.push({ id: '\u0001total', label: w.total, pick: {}, kind: 'total' })
  }

  // ---- rows
  const sortCol = sort ? cols.find((c) => c.id === sort.col) : undefined
  const ordered = (list: Member[], dim: DimId, parent: Spot): Member[] => {
    if (!sort || !sortCol || dim === 'period') return list
    const at = (m: Member) => value(joinSpot(parent, pickFor(dim, m.key)), sortCol.pick, sort.vi)
    return [...list].sort((a, b) => {
      const x = at(a)
      const y = at(b)
      if (x === null && y === null) return 0
      if (x === null) return 1
      if (y === null) return -1
      return (x - y) * sort.dir
    })
  }

  const rows: MRow[] = []
  const [outer, inner] = layout.rows
  if (outer) {
    for (const m of ordered(membersOf(outer), outer, {})) {
      const pick = pickFor(outer, m.key)
      rows.push({ id: `${outer}:${m.key}`, label: m.label, level: 0, group: !!inner, pick, dim: outer })
      if (inner) {
        for (const n of ordered(membersOf(inner), inner, pick)) {
          rows.push({
            id: `${outer}:${m.key}/${inner}:${n.key}`,
            label: n.label,
            level: 1,
            group: false,
            pick: joinSpot(pick, pickFor(inner, n.key)),
            dim: inner,
          })
        }
      }
    }
  }
  const total = layout.rows.includes('period') ? null : { id: '\u0001total', label: w.total, level: 0, group: false, pick: {} }
  return { rows, cols, total, value }
}

/** The rows a person sees: a collapsed group hides its children. */
export function visibleRows(rows: MRow[], collapsed: Set<string>): MRow[] {
  const out: MRow[] = []
  let hiding = ''
  for (const r of rows) {
    if (r.level === 0) {
      hiding = collapsed.has(r.id) ? r.id : ''
      out.push(r)
    } else if (!hiding) out.push(r)
  }
  return out
}

/** The first cell that opens its tickets, as [row, column], in reading order. */
export function firstOpen(opens: boolean[][]): [number, number] | null {
  for (let a = 0; a < opens.length; a++) for (let b = 0; b < (opens[a]?.length ?? 0); b++) if (opens[a]![b]) return [a, b]
  return null
}

/** Where a key moves the matrix's one tab stop: arrows to the next cell that opens
 *  that way, skipping inert ones; Home and End to the row's first and last; else null. */
export function roveStep(opens: boolean[][], [a, b]: [number, number], key: string): [number, number] | null {
  const row = opens[a] ?? []
  if (key === 'Home' || key === 'End') {
    const js = row.flatMap((on, j) => (on ? [j] : []))
    const j = key === 'Home' ? js[0] : js[js.length - 1]
    return j === undefined ? null : [a, j]
  }
  const way: Record<string, [number, number]> = { ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1] }
  const d = way[key]
  if (!d) return null
  for (let i = a + d[0], j = b + d[1]; i >= 0 && i < opens.length && j >= 0 && j < (opens[i]?.length ?? 0); i += d[0], j += d[1]) if (opens[i]![j]) return [i, j]
  return null
}

// ---------------------------------------------------------------- the canvas

/** How many of the canvas's six columns each tile asks for, wide (over 1360 px) and mid (1101–1360). */
export const TILE_SPAN: Record<string, { wide: number; mid: number }> = {
  hour: { wide: 3, mid: 3 },
  level: { wide: 3, mid: 3 },
  wait: { wide: 2, mid: 3 },
  desk: { wide: 2, mid: 3 },
  outcomes: { wide: 2, mid: 3 },
  bands: { wide: 3, mid: 3 },
  done: { wide: 3, mid: 3 },
  marks: { wide: 3, mid: 3 },
  matrix: { wide: 6, mid: 6 },
  time: { wide: 6, mid: 6 },
  breaks: { wide: 3, mid: 3 },
  feedback: { wide: 6, mid: 6 },
  fields: { wide: 3, mid: 3 },
  delivered: { wide: 3, mid: 3 },
}

/** Spans that leave no hole: tiles fill rows in order, and the last tile of a row
 *  the next one does not fit on widens to the row's end. */
export function packSpans(wants: number[], cols = 6): number[] {
  const out: number[] = []
  let used = 0
  wants.forEach((w, i) => {
    const want = Math.max(1, Math.min(w, cols))
    if (used > 0 && used + want > cols) out[i - 1]! += cols - used
    if (used + want > cols) used = 0
    out.push(want)
    used = (used + want) % cols
  })
  if (used > 0) out[out.length - 1]! += cols - used
  return out
}

/** Each shown tile's span, wide and mid, packed so the canvas has no holes. */
export function canvasSpans(shown: string[]): Record<string, { wide: number; mid: number }> {
  const want = (id: string) => TILE_SPAN[id] ?? { wide: 2, mid: 3 }
  const wide = packSpans(shown.map((id) => want(id).wide))
  const mid = packSpans(shown.map((id) => want(id).mid))
  return Object.fromEntries(shown.map((id, i) => [id, { wide: wide[i]!, mid: mid[i]! }]))
}

// ---------------------------------------------------------------- laying it out

export const sameLayout = (a: Layout, b: Layout) => JSON.stringify(a) === JSON.stringify(b)

export type Well = 'rows' | 'cols' | 'values'
export type DragSrc = { from: 'list'; field: FieldId } | { from: Well; index: number }

/**
 * The layout after a field is placed, moved or taken away, or null when the
 * move is not one the matrix can show.
 *
 * Two breakdowns at most, and two without the period: the server crosses two
 * groupings, not three, and not beside the period before. A field placed past
 * that takes another off, from the bar it was dropped on first.
 */
export function place(layout: Layout, src: DragSrc, to: Well | null, at?: number): Layout | null {
  const L: Layout = { rows: [...layout.rows], cols: [...layout.cols], values: layout.values.map((v) => ({ ...v })) }
  if (src.from === 'list') {
    const f = src.field
    if (isMeasure(f)) {
      if (!to) L.values = L.values.filter((v) => v.measure !== f)
      else {
        if (to !== 'values') return null
        if (L.values.length >= MAX_VALUES) return null
        L.values.splice(at ?? L.values.length, 0, isTimed(f) ? { measure: f, stat: 'median' } : { measure: f })
      }
    } else {
      if (to === 'values') return null
      let i = at ?? Infinity
      for (const w of ['rows', 'cols'] as const) {
        const j = L[w].indexOf(f)
        if (j >= 0) {
          L[w].splice(j, 1)
          if (w === to && j < i) i--
        }
      }
      if (to) {
        L[to].splice(Math.min(i, L[to].length), 0, f)
        const drop = (which: (d: DimId) => boolean) => {
          for (const w of [to, to === 'rows' ? 'cols' : 'rows'] as const) {
            for (let j = L[w].length - 1; j >= 0; j--) {
              if (L[w][j] !== f && which(L[w][j]!)) return void L[w].splice(j, 1)
            }
          }
        }
        const breaks = () => [...L.rows, ...L.cols].filter(isBreak).length
        while (breaks() > 2) drop(isBreak)
        if (breaks() === 2 && [...L.rows, ...L.cols].includes('period')) drop(f === 'period' ? isBreak : (d) => d === 'period')
      }
    }
  } else if (src.from === 'values') {
    if (to && to !== 'values') return null
    const [v] = L.values.splice(src.index, 1)
    if (to === 'values' && v) {
      const i = at !== undefined && at > src.index ? at - 1 : at ?? L.values.length
      L.values.splice(Math.min(i, L.values.length), 0, v)
    }
  } else {
    if (to === 'values') return null
    const [f] = L[src.from].splice(src.index, 1)
    if (to && f) {
      let i = at ?? L[to].length
      if (to === src.from && at !== undefined && at > src.index) i--
      L[to].splice(Math.min(i, L[to].length), 0, f)
    }
  }
  if (L.cols.length > MAX_COLS) {
    // A field dropped on a full Columns bar takes the place of the one there,
    // which goes to Rows if it fits.
    const [out] = L.cols.splice(to === 'cols' && at === 0 ? 1 : 0, 1)
    if (out && L.rows.length < MAX_ROWS) L.rows.push(out)
  }
  if (L.rows.length > MAX_ROWS) return null
  return sameLayout(L, layout) ? null : L
}

// ---------------------------------------------------------------- views

export type View = {
  id: string
  /** A built-in view's period as the last n whole days, ending yesterday. */
  wholeDays?: number
  /** The order a built-in view opens in. */
  sort?: MatrixSort
  /** A built-in view's name, as a phrase. */
  nameKey?: StaffKey
  /** A saved view's name, as its owner typed it. */
  name?: string
  layout: Layout
  period?: PeriodId
  custom?: [string, string]
  /** The visual a view opens: 'waits' focuses the waiting bands. */
  visual?: Visual
  /** A saved view's band edges. */
  bands?: BandSpec
}
export type Visual = 'waits'

export const OVERVIEW: Layout = {
  rows: ['service'],
  cols: [],
  values: [{ measure: 'arrived' }, { measure: 'served' }, { measure: 'wait', stat: 'median' }, { measure: 'level' }],
}

export const VIEWS: View[] = [
  { id: 'overview', nameKey: 'insight.view.overview', layout: OVERVIEW },
  { id: 'hours', nameKey: 'insight.view.hours', layout: { rows: ['hour'], cols: ['period'], values: [{ measure: 'arrived' }] } },
  {
    id: 'desks',
    nameKey: 'insight.view.desks',
    layout: { rows: ['desk'], cols: [], values: [{ measure: 'served' }, { measure: 'handling', stat: 'median' }, { measure: 'handling', stat: 'p90' }] },
  },
  {
    id: 'people',
    nameKey: 'insight.view.people',
    layout: { rows: ['person'], cols: [], values: [{ measure: 'served' }, { measure: 'handling', stat: 'median' }, { measure: 'handling', stat: 'p90' }] },
  },
  {
    id: 'compare',
    nameKey: 'insight.view.compare',
    layout: { rows: ['service'], cols: ['period'], values: [{ measure: 'level' }] },
  },
  {
    id: 'weekly',
    nameKey: 'insight.view.weekly',
    layout: { rows: ['week'], cols: [], values: [{ measure: 'arrived' }, { measure: 'wait', stat: 'median' }, { measure: 'level' }] },
    period: 'weeks12',
  },
  {
    id: 'monthly',
    nameKey: 'insight.view.monthly',
    layout: { rows: ['month'], cols: [], values: [{ measure: 'arrived' }, { measure: 'wait', stat: 'median' }, { measure: 'level' }] },
    period: 'months12',
  },
  // Four whole weeks to yesterday, so every weekday is counted four times.
  {
    id: 'weekdays',
    nameKey: 'insight.view.weekdays',
    layout: { rows: ['weekday'], cols: [], values: [{ measure: 'arrived' }, { measure: 'level' }] },
    period: 'custom',
    wholeDays: 28,
  },
  {
    id: 'busy15',
    nameKey: 'insight.view.busy15',
    layout: { rows: ['interval15'], cols: ['period'], values: [{ measure: 'arrived' }] },
    period: 'custom',
    wholeDays: 7,
    sort: { col: 'now', vi: 0, dir: -1 },
  },
  {
    id: 'channels',
    nameKey: 'insight.view.channels',
    layout: { rows: ['channel'], cols: [], values: [{ measure: 'arrived' }, { measure: 'wait', stat: 'median' }, { measure: 'level' }] },
    period: 'week',
  },
  { id: 'waits', nameKey: 'insight.view.waits', layout: OVERVIEW, period: 'week', visual: 'waits' },
]

/** The view the screen is showing: a built-in by its layout and visual, a saved
 *  one also by its period (when it keeps one) and its bands. */
export function currentView(views: View[], layout: Layout, period: PeriodId, custom: [string, string], visual: Visual | null, bands?: BandSpec): View | undefined {
  return views.find(
    (v) =>
      sameLayout(v.layout, layout) &&
      (v.visual ?? null) === visual &&
      (!v.name || !v.period || (v.period === period && (period !== 'custom' || JSON.stringify(v.custom) === JSON.stringify(custom)))) &&
      (!v.bands || !bands || sameBands(v.bands, bands)),
  )
}

const sameBands = (a: BandSpec, b: BandSpec) =>
  a.waitBy === b.waitBy && a.waitEdges.join() === b.waitEdges.join() && a.handlingEdges.join() === b.handlingEdges.join()

/** The period a view opens with, or null for one that keeps the period chosen. */
export function viewPeriod(v: View, today: string): { period: PeriodId; custom?: [string, string] } | null {
  if (v.wholeDays) return { period: 'custom', custom: [shiftDays(today, -v.wholeDays), shiftDays(today, -1)] }
  if (!v.period) return null
  return v.custom ? { period: v.period, custom: v.custom } : { period: v.period }
}

/** A layout read back from this browser's storage, checked field by field: a
 *  saved view from an older screen must not put a field the matrix no longer
 *  knows onto it. */
export function validLayout(x: unknown): Layout | null {
  if (!x || typeof x !== 'object') return null
  const l = x as Layout
  if (!Array.isArray(l.rows) || !Array.isArray(l.cols) || !Array.isArray(l.values)) return null
  if (![...l.rows, ...l.cols].every((d) => (DIMS as string[]).includes(d))) return null
  if (l.rows.length > MAX_ROWS || l.cols.length > MAX_COLS) return null
  const breaks = [...l.rows, ...l.cols].filter(isBreak).length
  if (breaks > 2 || (breaks === 2 && [...l.rows, ...l.cols].includes('period'))) return null
  if (new Set([...l.rows, ...l.cols]).size !== l.rows.length + l.cols.length) return null
  if (l.values.length > MAX_VALUES) return null
  if (!l.values.every((v) => v && isMeasure(v.measure) && (v.stat === undefined || (STATS as string[]).includes(v.stat)))) return null
  return { rows: [...l.rows], cols: [...l.cols], values: l.values.map((v) => (v.stat ? { measure: v.measure, stat: v.stat } : { measure: v.measure })) }
}

// ---------------------------------------------------------------- the network

export type NetMeasure = 'arrived' | 'served' | 'stillOpen' | 'wait' | 'handling' | 'level' | 'noShowRate'
export const NET_MEASURES: NetMeasure[] = ['arrived', 'served', 'stillOpen', 'wait', 'handling', 'level', 'noShowRate']

/** One figure off a line of the network comparison. The totals line has no
 *  medians: a median cannot be added up, and the mean of twelve medians is a
 *  number a head office would nonetheless put in a slide. */
export function netFigure(row: NetworkBranchLine, m: NetMeasure, total = false): number | null {
  const has = networkCells(row, total)
  switch (m) {
    case 'arrived':
      return row.issued
    case 'served':
      return row.served
    case 'stillOpen':
      return row.stillOpen
    case 'wait':
      return has.wait ? row.medianWaitSec : null
    case 'handling':
      return has.handling ? row.medianServeSec : null
    case 'level':
      return has.level ? row.withinTarget : null
    case 'noShowRate':
      return has.noShow ? row.noShowRate : null
  }
}

// ---------------------------------------------------------------- worksheets

const xfmt = (f: Fmt): XFmt => (f === 'dur' ? 'min' : f === 'pct' ? 'pct' : 'int')
/** Times go into the file in minutes, a number a spreadsheet can add up. */
const xval = (x: number | null, f: Fmt) => (x === null || !Number.isFinite(x) ? null : f === 'dur' ? Math.round((x / 60) * 100) / 100 : Math.round(x * 1e4) / 1e4)

/** A plain list: a heading line, a line saying what and when, then a table. */
export function listSheet(name: string, about: string, header: string[], rows: (string | number | null)[][], fmts: XFmt[]): XSheet {
  const out: XCell[][] = [
    [{ v: name, bold: true }],
    [{ v: about }],
    [],
    header.map((h, i) => ({ v: h, head: true, fmt: i === 0 ? 'text' : fmts[i] })),
    ...rows.map((r) => r.map((v, i) => ({ v: typeof v === 'number' ? Math.round(v * 1e4) / 1e4 : v, fmt: fmts[i] }))),
  ]
  const w0 = Math.min(40, Math.max(14, ...rows.map((r) => String(r[0] ?? '').length + 2)))
  return { name, rows: out, widths: [w0, ...header.slice(1).map((h) => Math.max(12, h.length + 2))], freeze: { rows: 4, cols: 1 } }
}

/** A list sheet with a first column naming the branch on its header and every row. */
export function withBranch(sheet: XSheet, head: string, branch: string): XSheet {
  const top = 3
  return {
    ...sheet,
    rows: sheet.rows.map((r, i) => (i < top ? r : [i === top ? { v: head, head: true, fmt: 'text' } : { v: branch, fmt: 'text' }, ...r])),
    widths: sheet.widths && [Math.min(40, Math.max(14, branch.length + 2)), ...sheet.widths],
    freeze: sheet.freeze && { ...sheet.freeze, cols: sheet.freeze.cols + 1 },
  }
}

/** A list whose figures are in the matrix's formats: times become minutes. */
export function figuresSheet(name: string, about: string, header: string[], rows: [string, ...(number | null)[]][], fmts: Fmt[], minutes: string): XSheet {
  return listSheet(
    name,
    about,
    header.map((h, i) => (i > 0 && fmts[i - 1] === 'dur' ? `${h} (${minutes})` : h)),
    rows.map(([label, ...xs]) => [label, ...xs.map((x, i) => xval(x, fmts[i] ?? 'int'))]),
    ['text', ...fmts.map(xfmt)],
  )
}

/** The matrix as it stands: column headers, indented row labels, the total. */
export function matrixSheet(
  m: Matrix,
  layout: Layout,
  flat: MRow[],
  name: string,
  about: string,
  valueNames: string[],
  corner: string,
  minutes: string,
): XSheet {
  const V = layout.values
  const fmts = V.map((v) => fmtOf(v.measure))
  const out: XCell[][] = [[{ v: name, bold: true }], [{ v: about }], []]
  const top = out.length
  const merges: [number, number, number, number][] = []
  const heads = V.map((v, i) => (fmtOf(v.measure) === 'dur' ? `${valueNames[i]} (${minutes})` : valueNames[i] ?? ''))
  const twoLines = layout.cols.length > 0
  if (twoLines) {
    const line: XCell[] = [{ v: null, head: true }]
    let c = 1
    for (const col of m.cols) {
      line[c] = { v: col.label, head: true }
      for (let k = 1; k < V.length; k++) line[c + k] = { v: null, head: true }
      if (V.length > 1) merges.push([top, c, top, c + V.length - 1])
      c += V.length
    }
    out.push(line)
  }
  out.push([{ v: corner, head: true }, ...m.cols.flatMap(() => heads.map((h) => ({ v: h, head: true })))])
  const body = (r: MRow, bold: boolean) => {
    const row: XCell[] = [{ v: r.label, bold, indent: r.level }]
    for (const c of m.cols) {
      V.forEach((_, vi) => {
        const f = fmts[vi] ?? 'int'
        row.push({ v: xval(m.value(r.pick, c.pick, vi), f), bold: bold || c.kind !== 'leaf', fmt: xfmt(f) })
      })
    }
    return row
  }
  for (const r of flat) out.push(body(r, r.group))
  if (m.total) out.push(body(m.total, true))
  const labelWidth = Math.min(48, Math.max(14, ...flat.map((r) => r.label.length + r.level * 2 + 2)))
  return {
    name,
    rows: out,
    merges,
    widths: [labelWidth, ...m.cols.flatMap(() => heads.map((h) => Math.max(12, h.length / 1.2)))],
    freeze: { rows: top + (twoLines ? 2 : 1), cols: 1 },
  }
}
